import React, { useState } from "react";
import { Check } from "lucide-react";

import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/shared/components/ui/dialog";

/** One row of the picker. Generic in the id so a caller keeps whatever id
    family it already has. */
export interface PickerItem<Id extends string> {
  id: Id;
  icon?: React.ReactNode;
  label: string;
  desc?: string;
  right?: React.ReactNode;
}

// Generic multi-select picker: search, checkbox rows, "Add N" footer.
// items: [{ id, icon (node), label, desc, right (node) }]
export default function AddItemsDialog<Id extends string>({ open, onOpenChange, title, subtitle, noun, items, onAdd }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle: string;
  noun: string;
  items: PickerItem<Id>[];
  onAdd: (ids: Id[]) => void;
}) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<Id[]>([]);
  React.useEffect(() => { if (open) { setQ(""); setSel([]); } }, [open]);
  const filtered = items.filter((i) =>
    `${i.label} ${i.desc || ""}`.toLowerCase().includes(q.toLowerCase())
  );
  const toggle = (id: Id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const add = () => {
    if (!sel.length) return;
    onAdd(sel);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 w-[440px] max-w-[94vw] rounded-[16px] overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog">
        <div className="px-5 pt-5 pb-3">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{title}</DialogTitle>
          <DialogDescription className="text-[13px] text-[var(--text-tertiary)] mt-0.5">{subtitle}</DialogDescription>
        </div>
        <div className="px-5 pb-3">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${noun}s…`}
            className="w-full h-9 px-3 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[14px] outline-none focus:ring-[var(--hairline-focus)] placeholder:text-[var(--text-quaternary)] tracking-[-0.005em]"
          />
        </div>
        <div className="mx-5 mb-4 rounded-[10px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] overflow-hidden">
          <div className="max-h-[300px] overflow-y-auto divide-y divide-[var(--hairline)]">
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-[12.5px] text-[var(--text-quaternary)]">
                No {noun}s match “{q}”
              </div>
            )}
            {filtered.map((i) => {
              const selected = sel.includes(i.id);
              return (
                <button
                  key={i.id}
                  onClick={() => toggle(i.id)}
                  className={`group w-full flex items-center gap-2.5 px-3 py-2 text-left min-w-0 transition-colors ${selected ? "bg-[var(--surface-sunk)]/70" : "hover:bg-[var(--surface-sunk)]/45"}`}
                >
                  <span className={`shrink-0 w-[15px] h-[15px] rounded-[4px] flex items-center justify-center transition-colors ${selected ? "bg-[var(--brand)]" : "ring-1 ring-inset ring-[var(--hairline-strong)] group-hover:ring-[var(--hairline-focus)]"}`}>
                    {selected && <Check className="w-[9px] h-[9px] text-[var(--brand-foreground)] fade-in" strokeWidth={3.5} />}
                  </span>
                  {i.icon}
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="text-[13px] font-medium tracking-[-0.005em] truncate text-[var(--text-primary)]">{i.label}</span>
                      {i.right}
                    </span>
                    {i.desc && (
                      <span className="block text-[11.5px] text-[var(--text-tertiary)] truncate mt-[1px]">{i.desc}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="px-5 py-3.5 border-t hairline bg-[var(--surface)] flex items-center justify-end gap-2">
          <button
            onClick={() => onOpenChange(false)}
            className="press h-[30px] px-3 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--canvas)] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]"
          >
            Cancel
          </button>
          <button
            onClick={add}
            disabled={!sel.length}
            className="press h-[30px] px-3.5 rounded-[8px] bg-[var(--brand)] text-[var(--brand-foreground)] text-[13px] font-medium hover:bg-[var(--brand-hover)] disabled:opacity-40 disabled:pointer-events-none"
          >
            Add {sel.length > 1 ? `${sel.length} ${noun}s` : noun}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
